import { useState, useRef, useCallback } from 'react'
import { Upload, X, FileSpreadsheet, Loader2, AlertTriangle } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { useUploadPreview, useUploadCampaignList } from '../../api/campaigns'
import { cn } from '../../lib/utils'

interface CampaignUploadModalProps {
  open: boolean
  onClose: () => void
  onSuccess?: () => void
}

type Step = 'pick' | 'preview' | 'uploading'

export function CampaignUploadModal({ open, onClose, onSuccess }: CampaignUploadModalProps) {
  const { t } = useTranslation('campaigns')
  const [step, setStep] = useState<Step>('pick')
  const [file, setFile] = useState<File | null>(null)
  const [listName, setListName] = useState('')
  const [phoneCol, setPhoneCol] = useState<string>('')
  const [nameCol, setNameCol] = useState<string>('')
  const [lastNameCol, setLastNameCol] = useState<string>('')
  const [previewData, setPreviewData] = useState<{
    headers: string[]
    detected_mapping: { phone: string | null; name: string | null; last_name: string | null }
    preview_rows: Record<string, string>[]
    total_rows: number
  } | null>(null)

  const fileInputRef = useRef<HTMLInputElement>(null)
  const preview = useUploadPreview()
  const upload = useUploadCampaignList()

  const reset = useCallback(() => {
    setStep('pick')
    setFile(null)
    setListName('')
    setPhoneCol('')
    setNameCol('')
    setLastNameCol('')
    setPreviewData(null)
    preview.reset()
    upload.reset()
  }, [preview, upload])

  const handleClose = () => {
    reset()
    onClose()
  }

  const handleFilePick = async (picked: File) => {
    setFile(picked)
    setListName(picked.name.replace(/\.(csv|xlsx)$/i, ''))

    try {
      const data = await preview.mutateAsync(picked)
      setPreviewData(data)
      setPhoneCol(data.detected_mapping.phone || '')
      setNameCol(data.detected_mapping.name || '')
      setLastNameCol(data.detected_mapping.last_name || '')
      setStep('preview')
    } catch {
      // Error is in preview.error
    }
  }

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault()
    const dropped = e.dataTransfer.files[0]
    if (dropped) handleFilePick(dropped)
  }

  const handleUpload = async () => {
    if (!file || !phoneCol) return
    setStep('uploading')
    try {
      await upload.mutateAsync({
        file,
        name: listName || file.name,
        phoneColumn: phoneCol,
        nameColumn: nameCol || undefined,
        lastNameColumn: lastNameCol || undefined,
      })
      onSuccess?.()
      handleClose()
    } catch {
      setStep('preview')
    }
  }

  if (!open) return null

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="fixed inset-0 bg-black/50" onClick={handleClose} />
      <div className="relative z-10 flex w-full max-w-2xl flex-col rounded-xl border border-border bg-surface-card shadow-xl" style={{ maxHeight: 'min(90vh, 640px)' }}>
        {/* Header */}
        <div className="flex items-center justify-between border-b border-border px-6 py-4">
          <h2 className="text-lg font-semibold text-text-primary">{t('upload.title')}</h2>
          <button onClick={handleClose} className="rounded-lg p-1 text-text-muted hover:text-text-primary">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto px-6 py-5">
          {/* Step 1: File picker */}
          {step === 'pick' && (
            <div
              onDragOver={(e) => e.preventDefault()}
              onDrop={handleDrop}
              className={cn(
                'flex flex-col items-center justify-center rounded-lg border-2 border-dashed p-10 transition-colors',
                preview.isPending
                  ? 'border-primary/50 bg-primary/5'
                  : 'border-border hover:border-primary/50 hover:bg-surface-elevated/50'
              )}
            >
              {preview.isPending ? (
                <>
                  <Loader2 className="h-10 w-10 animate-spin text-primary" />
                  <p className="mt-3 text-sm text-text-secondary">{t('upload.analyzing')}</p>
                </>
              ) : (
                <>
                  <Upload className="h-10 w-10 text-text-muted" />
                  <p className="mt-3 text-sm text-text-secondary">
                    {t('upload.dragDrop')}
                  </p>
                  <button
                    onClick={() => fileInputRef.current?.click()}
                    className="mt-2 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-white hover:bg-primary/90"
                  >
                    {t('upload.browseFiles')}
                  </button>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".csv,.xlsx"
                    className="hidden"
                    onChange={(e) => {
                      const f = e.target.files?.[0]
                      if (f) handleFilePick(f)
                    }}
                  />
                </>
              )}
              {preview.isError && (
                <p className="mt-3 text-sm text-error">
                  {(preview.error as Error).message}
                </p>
              )}
            </div>
          )}

          {/* Step 2: Preview + column mapping */}
          {step === 'preview' && previewData && (
            <div className="space-y-5">
              {/* File info */}
              <div className="flex items-center gap-3 rounded-lg bg-surface-elevated px-4 py-3">
                <FileSpreadsheet className="h-5 w-5 text-primary" />
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium text-text-primary">{file?.name}</p>
                  <p className="text-xs text-text-muted">{t('upload.rowsDetected', { count: previewData.total_rows })}</p>
                </div>
                <button
                  onClick={reset}
                  className="text-xs text-text-muted hover:text-error"
                >
                  {t('upload.changeFile')}
                </button>
              </div>

              {/* List name */}
              <div>
                <label className="mb-1 block text-sm font-medium text-text-secondary">
                  {t('upload.campaignName')}
                </label>
                <input
                  type="text"
                  value={listName}
                  onChange={(e) => setListName(e.target.value)}
                  className="w-full rounded-lg border border-border bg-surface px-3 py-2 text-sm text-text-primary placeholder:text-text-muted focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
                  placeholder={t('upload.campaignNamePlaceholder')}
                />
              </div>

              {/* Column mapping */}
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="mb-1 block text-sm font-medium text-text-secondary">
                    {t('upload.phoneColumn')} <span className="text-error">*</span>
                  </label>
                  <select
                    value={phoneCol}
                    onChange={(e) => setPhoneCol(e.target.value)}
                    className="w-full rounded-lg border border-border bg-surface px-3 py-2 text-sm text-text-primary focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
                  >
                    <option value="">{t('upload.selectColumn')}</option>
                    {previewData.headers.map((h) => (
                      <option key={h} value={h}>{h}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="mb-1 block text-sm font-medium text-text-secondary">
                    {t('upload.firstNameColumn')}
                  </label>
                  <select
                    value={nameCol}
                    onChange={(e) => setNameCol(e.target.value)}
                    className="w-full rounded-lg border border-border bg-surface px-3 py-2 text-sm text-text-primary focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
                  >
                    <option value="">{t('upload.none')}</option>
                    {previewData.headers.map((h) => (
                      <option key={h} value={h}>{h}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="mb-1 block text-sm font-medium text-text-secondary">
                    {t('upload.lastNameColumn')}
                  </label>
                  <select
                    value={lastNameCol}
                    onChange={(e) => setLastNameCol(e.target.value)}
                    className="w-full rounded-lg border border-border bg-surface px-3 py-2 text-sm text-text-primary focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
                  >
                    <option value="">{t('upload.none')}</option>
                    {previewData.headers.map((h) => (
                      <option key={h} value={h}>{h}</option>
                    ))}
                  </select>
                </div>
              </div>

              {!phoneCol && (
                <div className="flex items-center gap-2 rounded-lg bg-warning/10 px-3 py-2 text-sm text-warning">
                  <AlertTriangle className="h-4 w-4 shrink-0" />
                  {t('upload.selectPhonePrompt')}
                </div>
              )}

              {/* Preview table */}
              <div>
                <p className="mb-2 text-xs font-medium text-text-muted">{t('upload.previewLabel')}</p>
                <div className="max-h-40 overflow-auto rounded-lg border border-border">
                  <table className="w-full text-sm">
                    <thead className="sticky top-0">
                      <tr className="border-b border-border bg-surface-elevated">
                        {previewData.headers.map((h) => (
                          <th
                            key={h}
                            className={cn(
                              'px-3 py-1.5 text-left text-xs font-medium whitespace-nowrap',
                              h === phoneCol
                                ? 'text-primary'
                                : h === nameCol || h === lastNameCol
                                ? 'text-info'
                                : 'text-text-muted'
                            )}
                          >
                            {h}
                            {h === phoneCol && ' 📞'}
                            {h === nameCol && ' 👤'}
                            {h === lastNameCol && ' 👤'}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {previewData.preview_rows.slice(0, 3).map((row, i) => (
                        <tr key={i} className="border-b border-border/50">
                          {previewData.headers.map((h) => (
                            <td key={h} className="px-3 py-1 text-xs text-text-secondary whitespace-nowrap">
                              {row[h] || '—'}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {upload.isError && (
                <p className="text-sm text-error">
                  {(upload.error as Error).message}
                </p>
              )}
            </div>
          )}

          {/* Step 3: Uploading */}
          {step === 'uploading' && (
            <div className="flex flex-col items-center justify-center py-12">
              <Loader2 className="h-10 w-10 animate-spin text-primary" />
              <p className="mt-3 text-sm text-text-secondary">{t('upload.creating')}</p>
            </div>
          )}
        </div>

        {/* Footer */}
        {step === 'preview' && (
          <div className="flex shrink-0 items-center justify-end gap-3 border-t border-border px-6 py-4">
            <button
              onClick={handleClose}
              className="rounded-lg px-4 py-2 text-sm font-medium text-text-secondary hover:bg-surface-elevated"
            >
              {t('common:actions.cancel')}
            </button>
            <button
              onClick={handleUpload}
              disabled={!phoneCol}
              className={cn(
                'rounded-lg px-5 py-2 text-sm font-medium text-white transition-colors',
                phoneCol
                  ? 'bg-primary hover:bg-primary/90'
                  : 'cursor-not-allowed bg-primary/50'
              )}
            >
              {t('upload.importContacts', { count: previewData?.total_rows ?? 0 })}
            </button>
          </div>
        )}
      </div>
    </div>
  )
}
